/**
 * EntityForm constants and default values.
 *
 * Centralises default configuration, size mappings, and layout tokens
 * used across the EntityForm component tree.
 */

import type {
  LayoutConfig,
  ActionsConfig,
  EntityFormI18nOverrides,
} from './types';
import type { ResponsiveColumns } from '@components/base/layout/Grid';

// ---------------------------------------------------------------------------
// Layout Defaults
// ---------------------------------------------------------------------------

/**
 * Default layout configuration applied when no layout is specified
 * in the EntityFormConfig.
 */
export const DEFAULT_LAYOUT: Required<LayoutConfig> = {
  columns: 1,
  density: 'default',
  fieldSize: 'md',
  gap: 'md',
} as const;

// ---------------------------------------------------------------------------
// Actions Defaults
// ---------------------------------------------------------------------------

/**
 * Default actions configuration applied when no actions are specified
 * in the EntityFormConfig.
 */
export const DEFAULT_ACTIONS: ActionsConfig = {
  submit: {
    visible: true,
    label: 'Submit',
    variant: 'primary',
  },
  cancel: {
    visible: true,
    label: 'Cancel',
    variant: 'ghost',
  },
  reset: {
    visible: false,
  },
  alignment: 'right',
} as const;

// ---------------------------------------------------------------------------
// Field Size Mapping
// ---------------------------------------------------------------------------

/**
 * Maps density settings to the default field size.
 * Compact forms use small inputs, comfortable forms use large ones.
 */
export const DENSITY_TO_FIELD_SIZE: Record<
  NonNullable<LayoutConfig['density']>,
  NonNullable<LayoutConfig['fieldSize']>
> = {
  compact: 'sm',
  default: 'md',
  comfortable: 'lg',
} as const;

/**
 * Alias kept for descriptive use where "field size map" is the clearer name.
 */
export const FIELD_SIZE_MAP = DENSITY_TO_FIELD_SIZE;

// ---------------------------------------------------------------------------
// Gap Size Mapping
// ---------------------------------------------------------------------------

/**
 * Maps gap size tokens to Tailwind gap utility classes.
 */
export const GAP_SIZE_MAP: Record<NonNullable<LayoutConfig['gap']>, string> = {
  sm: 'gap-4',
  md: 'gap-5',
  lg: 'gap-7',
} as const;

// ---------------------------------------------------------------------------
// Column Class Mapping
// ---------------------------------------------------------------------------

/**
 * Maps column count to Grid component ResponsiveColumns configuration.
 * Uses responsive breakpoints: single column on mobile, configured columns
 * from the `md` breakpoint upward.
 */
export const COLUMN_CLASS_MAP: Record<
  NonNullable<LayoutConfig['columns']>,
  ResponsiveColumns
> = {
  1: { base: 1 },
  2: { base: 1, md: 2 },
  3: { base: 1, md: 2, lg: 3 },
  4: { base: 1, md: 2, lg: 4 },
} as const;

// ---------------------------------------------------------------------------
// Default i18n Keys
// ---------------------------------------------------------------------------

/**
 * Default i18n key patterns used when no overrides are provided.
 * These follow a consistent `entityForm.<token>` namespace.
 */
export const DEFAULT_I18N_KEYS: Required<
  Pick<
    EntityFormI18nOverrides,
    'submitLabel' | 'cancelLabel' | 'resetLabel'
  >
> & {
  validation: Required<NonNullable<EntityFormI18nOverrides['validation']>>;
} = {
  submitLabel: 'entityForm.submit',
  cancelLabel: 'entityForm.cancel',
  resetLabel: 'entityForm.reset',
  validation: {
    required: 'entityForm.validation.required',
    minLength: 'entityForm.validation.minLength',
    maxLength: 'entityForm.validation.maxLength',
    min: 'entityForm.validation.min',
    max: 'entityForm.validation.max',
    pattern: 'entityForm.validation.pattern',
    email: 'entityForm.validation.email',
  },
} as const;
